// src/components/CreateEngineer.js
import React, { useState } from 'react';
import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  TextField,
  Button,
  Box,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  Collapse,
  Divider,
  Tooltip,
  LinearProgress,
  Chip,
  FormHelperText,
} from '@mui/material';

import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import EngineeringIcon from '@mui/icons-material/Engineering';
import InfoIcon from '@mui/icons-material/Info';

function CreateEngineer({ onEngineerCreated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});

  // Password strength indicator
  const getPasswordStrength = (pwd) => {
    if (!pwd) return { strength: 0, label: '', color: 'default' };
    if (pwd.length < 6) return { strength: 25, label: 'Too Short', color: 'error' };
    if (pwd.length < 8) return { strength: 50, label: 'Weak', color: 'warning' };
    if (pwd.length < 12) return { strength: 75, label: 'Good', color: 'info' };
    return { strength: 100, label: 'Strong', color: 'success' };
  };

  const passwordStrength = getPasswordStrength(password);

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Validate
    if (!validateForm()) {
      setMessage({ 
        type: 'error', 
        text: 'Please fix the errors before submitting' 
      });
      return;
    }

    setIsLoading(true);

    try {
      const createNewEngineer = httpsCallable(functions, 'createNewEngineer');
      const result = await createNewEngineer({
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });

      console.log('createNewEngineer result:', result);

      setMessage({
        type: 'success',
        text: `✓ Engineer account created for ${firstName} ${lastName}!`
      });

      // Reset form
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setErrors({});

      // Callback if provided
      if (onEngineerCreated) {
        onEngineerCreated({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
        });
      }

      // Auto-hide success message after 3 seconds
      setTimeout(() => setMessage(null), 3000);

    } catch (error) {
      console.error('Error calling createNewEngineer:', error);

      const code = error.code || 'unknown';
      let msg = error.message?.replace('FirebaseError: ', '') || 
                'An unknown error occurred while creating the engineer.';

      // User-friendly error messages
      if (code.includes('already-exists') || msg.includes('already exists')) {
        msg = 'An account with this email already exists';
      } else if (code.includes('invalid-argument')) {
        msg = 'Invalid input provided. Please check all fields';
      } else if (code.includes('permission-denied')) {
        msg = 'You do not have permission to create engineer accounts';
      } else if (code.includes('unauthenticated')) {
        msg = 'You must be logged in to create engineer accounts';
      }

      setMessage({
        type: 'error',
        text: msg
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setErrors({});
    setMessage(null);
  };

  const isFormEmpty = !firstName && !lastName && !email && !password;

  return (
    <Box>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {/* Name Fields */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (errors.firstName) {
                  setErrors({ ...errors, firstName: null });
                }
              }}
              fullWidth
              required
              error={!!errors.firstName}
              helperText={errors.firstName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" color={errors.firstName ? "error" : "action"} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                if (errors.lastName) {
                  setErrors({ ...errors, lastName: null });
                }
              }}
              fullWidth
              required
              error={!!errors.lastName}
              helperText={errors.lastName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" color={errors.lastName ? "error" : "action"} />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>

          {/* Email */}
          <TextField
            type="email"
            label="Engineer's Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) {
                setErrors({ ...errors, email: null });
              }
            }}
            fullWidth
            required
            error={!!errors.email}
            helperText={errors.email || "This will be their login username"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" color={errors.email ? "error" : "action"} />
                </InputAdornment>
              ),
            }}
          />

          {/* Password */}
          <Box>
            <TextField
              type={showPassword ? 'text' : 'password'}
              label="Temporary Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password) {
                  setErrors({ ...errors, password: null });
                }
              }}
              fullWidth
              required
              error={!!errors.password}
              helperText={errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" color={errors.password ? "error" : "action"} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Password Strength Indicator */}
            {password && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={passwordStrength.strength}
                    color={passwordStrength.color}
                    sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                  />
                  <Chip 
                    label={passwordStrength.label} 
                    size="small" 
                    color={passwordStrength.color}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
                <FormHelperText>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoIcon sx={{ fontSize: 14 }} />
                    Engineer can change this password after first login
                  </Box>
                </FormHelperText>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth 
              disabled={isLoading}
              startIcon={isLoading ? null : <EngineeringIcon />}
              sx={{ 
                py: 1.2,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                },
              }}
            >
              {isLoading ? 'Creating Account...' : 'Create Engineer'}
            </Button>

            {!isFormEmpty && (
              <Tooltip title="Clear form">
                <IconButton 
                  onClick={handleClear}
                  disabled={isLoading}
                  sx={{ 
                    border: '1px solid rgba(148,163,184,0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(248,113,113,0.1)',
                    },
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Loading Progress */}
          {isLoading && (
            <LinearProgress 
              sx={{ 
                borderRadius: 2,
                height: 4,
              }} 
            />
          )}

          {/* Success/Error Message */}
          <Collapse in={!!message}>
            {message && (
              <Alert 
                severity={message.type}
                sx={{ 
                  animation: 'fadeIn 0.3s ease-in',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(-10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                {message.text}
              </Alert>
            )}
          </Collapse>

          {/* Info Box */}
          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 1 }}>
            <strong>Note:</strong> The engineer will receive their login credentials and can access their dashboard immediately after account creation.
          </Alert>
        </Stack>
      </Box>
    </Box>
  );
}

export default CreateEngineer;